import { Flex } from "@mantine/core";
import { useCallback, useEffect } from "react";

import { useSalesContext } from "../../../sales-context/SalesContext";
import { useProductOfferStore } from "../../../stores";
import { EligibilityStatus, SaleProgress } from "../../../types";
import { LongFormAiPanel } from "../../long-form-ai/LongFormAiPanel/LongFormAiPanel";
import { shouldShowLongFormAi } from "../../long-form-ai/shouldShowLongFormAi";
import { IntegratedFlow } from "./IntegratedFlow";
import { useInitDisclosureLanguage } from "./language-selector/useInitDisclosureLanguage";
import { EnrollFailureScreen } from "./StepSpecific/EnrollFailure";
import { SaleView } from "./StepSpecific/SaleView";

export function ProductSaleFlow() {
    const { sessionId, analytics, features, taskType } = useSalesContext();
    const selectedProduct = useProductOfferStore((state) => state.getSelectedProduct(sessionId));

    // Long Form AI is a peer surface to the legacy offer-checklist flow, chosen right here so the
    // host mounts one ProductSaleFlow and never a separate pane (openspec long-form-ai, task 1.12).
    // It replaces the checklist ONLY for the VHDP pilot offer, under the same trigger the checklist
    // uses (an eligible product being actively sold). The flag alone decides narrative-vs-checklist —
    // there is no in-surface fallback to the checklist anymore (product dropped it).
    const showLongFormAi = shouldShowLongFormAi({
        isLongFormAiEnabled: features.isLongFormAiEnabled,
        selectedProduct,
        taskType,
    });

    // Seed the disclosure language from the task locale once per session (expert can override).
    useInitDisclosureLanguage();

    useEffect(() => {
        void analytics.dispatcher.dispatchAction("PageShown", "sale-tool");
    }, [analytics.dispatcher]);

    const { getProductInfo } = useProductOfferStore.getState();

    const handleOnPrimarySaleComplete = useCallback(() => {
        useProductOfferStore.getState().updateSelectedProduct(sessionId, undefined);
    }, [sessionId]);

    const legacyContent =
        selectedProduct?.saleProgress === SaleProgress.InProgress &&
        selectedProduct.status === EligibilityStatus.Eligible ? (
            <IntegratedFlow product={selectedProduct} />
        ) : (
            <Flex data-testid="product-sale-flow-container" h={"100%"} w={"100%"}>
                {/* Endgame */}
                {selectedProduct?.saleProgress === SaleProgress.Success ||
                selectedProduct?.saleProgress === SaleProgress.Declined ? (
                    <SaleView
                        onPrimarySaleComplete={handleOnPrimarySaleComplete}
                        status={selectedProduct.saleProgress}
                        productInfo={getProductInfo(sessionId, selectedProduct.productSku)}
                    />
                ) : null}

                {/* Show SaleView when product is not eligible */}
                {selectedProduct &&
                selectedProduct.status !== EligibilityStatus.Eligible &&
                selectedProduct.saleProgress !== SaleProgress.Failure ? (
                    <SaleView
                        onPrimarySaleComplete={handleOnPrimarySaleComplete}
                        status={selectedProduct.status}
                        productInfo={getProductInfo(sessionId, selectedProduct.productSku)}
                    />
                ) : null}

                {/* Alternate Endgame */}
                {selectedProduct?.saleProgress === SaleProgress.Failure ? (
                    <EnrollFailureScreen isProductAsurionBilled={selectedProduct.isProductAsurionBilled} />
                ) : null}
            </Flex>
        );

    if (showLongFormAi) {
        return <LongFormAiPanel />;
    }

    // Flag off, a non-VHDP offer, or a non-voice/chat task: the legacy offer-checklist flow renders
    // unchanged. The flag alone selects the surface — there is no in-session fallback to the checklist.
    return legacyContent;
}
